import { takeEvery, put, call } from 'redux-saga/effects'
import { SagaIterator } from 'redux-saga'
import { Action } from 'typescript-fsa'

import { CollectionItem, Collection } from '../api/types'
import {
	fetchCollectionItems,
	CollectionItemsRequestParams,
	LoadCollectionItemsFromNetworkResult,
	fetchCollection,
	CollectionRequestParams,
	LoadCollectionFromNetworkResult,
} from '../api/actions'
import {
	loadedCollectionItemsFromDisk,
	loadCollectionItems,
	LoadCollectionItemsParams,
	loadCollectionDetails,
	LoadCollectionDetailsPayload,
	loadedCollectionDetailsFromDisk,
} from './actions'
import { store } from '../root/index'
import { getConfiguration } from '../configuration/index'

/**
 * Triggers the loading of a collection items.
 */
// Items cached before a tour needed its screens carry no `screen` key at all, so for a full request
// they are a miss rather than a hit with holes.
const carryScreens = (items: CollectionItem[]): boolean => items.every(item => 'screen' in item)

function* handleLoadCollectionItems(action: LoadCollectionItemsParams): SagaIterator {
	const { id, version, language, subtype, accountId, full } = action.payload
	const state = store.getState()
	const { projectId } = state.settings.appInfo
	const platform = getConfiguration().getPlatform()
	const deviceUUID = getConfiguration().getDeviceUUID()

	// check if exists on Disk
	const items: CollectionItem[] | undefined = yield call(
		getConfiguration().loadCollectionItemsFromDisk,
		projectId,
		id,
		version,
		language,
	)
	if (items && (!full || carryScreens(items))) {
		// store onto redux state
		yield put(loadedCollectionItemsFromDisk({ id, version, items, language, subtype }))
	} else {
		// else pull down json from network
		// send the async action to start the network request
		const params: CollectionItemsRequestParams = {
			devicePlatform: platform,
			deviceUdid: deviceUUID,
			collectionId: id,
			collectionVersion: version,
			accountId,
			collectionSubtype: subtype,
			language,
			full,
			isPreCaching: false,
		}
		yield put(fetchCollectionItems.started(params))
	}
}

function* handleLoadCollectionDetails(action: Action<LoadCollectionDetailsPayload>): SagaIterator {
	const { id, version, language, accountId } = action.payload
	const state = store.getState()
	const { projectId } = state.settings.appInfo
	const platform = getConfiguration().getPlatform()
	const deviceUUID = getConfiguration().getDeviceUUID()

	// check if exists on Disk
	const item: Collection | undefined = yield call(
		getConfiguration().loadCollectionDetailsFromDisk,
		projectId,
		id,
		version,
		language,
	)
	if (item) {
		// store onto redux state
		yield put(loadedCollectionDetailsFromDisk({ id, version, collection: item, language }))
	} else {
		// else pull down json from network
		// send the async action to start the network request
		const params: CollectionRequestParams = {
			devicePlatform: platform,
			deviceUdid: deviceUUID,
			collectionId: id,
			collectionVersion: version,
			accountId,
			language,
			isPreCaching: false,
		}
		yield put(fetchCollection.started(params))
	}
}

/**
 * After a successful request to fetch collection items we want to store the result to disk so that we do not need to hit the network again (unless the version changes).
 * @param action contains the collection items retrieved from network response, and the collection id + version
 */
function* handleLoadedCollectionItemsFromNetwork(action: LoadCollectionItemsFromNetworkResult): SagaIterator {
	const { collectionId, collectionVersion, language } = action.payload.params
	const items: CollectionItem[] | undefined = action.payload.result.data
	const state = store.getState()
	const { projectId } = state.settings.appInfo

	// first delete any old collection items versions from disk
	try {
		yield call(getConfiguration().removeCollectionItemsFromDisk, projectId, collectionId, language)
	} catch (error) {
		// Error deleting old data
	}

	try {
		yield call(
			getConfiguration().saveCollectionItemsToDisk,
			projectId,
			collectionId,
			collectionVersion,
			language,
			items,
		)
	} catch (error) {
		// Error saving data
	}
}

function* handleLoadedCollectionDetailsFromNetwork(action: LoadCollectionFromNetworkResult): SagaIterator {
	const { collectionId, collectionVersion, language } = action.payload.params
	const collection: Collection | undefined = action.payload.result
	const state = store.getState()
	const { projectId } = state.settings.appInfo

	// first delete any old collection details versions from disk
	try {
		yield call(getConfiguration().removeCollectionDetailsFromDisk, projectId, collectionId, language)
	} catch (error) {
		// Error deleting old data
	}

	try {
		yield call(
			getConfiguration().saveCollectionDetailsToDisk,
			projectId,
			collectionId,
			collectionVersion,
			language,
			collection,
		)
	} catch (error) {
		// Error saving data
	}
}

export default function* (): SagaIterator {
	yield takeEvery(loadCollectionItems, handleLoadCollectionItems)
	yield takeEvery(loadCollectionDetails, handleLoadCollectionDetails)

	// when collection items are loaded from network, store to disk
	yield takeEvery(fetchCollectionItems.done, handleLoadedCollectionItemsFromNetwork)
	yield takeEvery(fetchCollection.done, handleLoadedCollectionDetailsFromNetwork)
}
